import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Statistic, Progress, Typography, Spin, Space, Alert } from 'antd';
import { 
  CreditCardOutlined, 
  FileTextOutlined, 
  RiseOutlined,
  DollarOutlined,
  CalendarOutlined,
  WarningOutlined,
  TeamOutlined,
  ShopOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { userService, companiesService, certificatesService } from '../../services/api';

const { Title, Text } = Typography;

const parseMysqlDateTime = (value) => {
  if (!value) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  if (raw.includes('T')) {
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const parts = raw.split(/[- :]/).filter(Boolean);
  if (parts.length < 3) return null;
  const y = Number(parts[0]);
  const m = Number(parts[1]);
  const d = Number(parts[2]);
  const hh = parts.length >= 4 ? Number(parts[3]) : 0;
  const mm = parts.length >= 5 ? Number(parts[4]) : 0;
  const ss = parts.length >= 6 ? Number(parts[5]) : 0;
  if (!y || !m || !d) return null;
  const date = new Date(y, m - 1, d, hh, mm, ss);
  return Number.isNaN(date.getTime()) ? null : date;
};

const computeCsdStatus = (certificates = []) => {
  const now = new Date();
  const list = Array.isArray(certificates) ? certificates : [];
  if (list.length === 0) {
    return { status: 'missing', expiresAt: null, daysLeft: null };
  }

  const certsWithExp = list
    .map((c) => ({
      cert: c,
      exp: parseMysqlDateTime(c?.expiration_date),
    }))
    .filter((x) => x.exp);

  if (certsWithExp.length === 0) {
    return { status: 'unknown', expiresAt: null, daysLeft: null };
  }

  const latest = certsWithExp.reduce((acc, cur) => (cur.exp > acc.exp ? cur : acc), certsWithExp[0]);
  const hasValid = certsWithExp.some((x) => x.exp.getTime() >= now.getTime());
  const daysLeft = Math.ceil((latest.exp.getTime() - now.getTime()) / (24 * 60 * 60 * 1000));

  if (!hasValid) return { status: 'expired', expiresAt: latest.exp, daysLeft };
  if (daysLeft <= 30) return { status: 'expiring_soon', expiresAt: latest.exp, daysLeft };
  return { status: 'valid', expiresAt: latest.exp, daysLeft };
};

const UserDashboard = () => {
  const { user } = useAuth();
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    const fetchBalance = async () => {
      try {
        const balanceData = await userService.getBalance();
        setBalance(balanceData);
      } catch (error) {
        console.error('Error fetching balance:', error);
      } finally {
        setLoading(false);
      }
    };

    const fetchCompanies = async () => {
      try {
        const response = await companiesService.getCompanies();
        const companiesList = response.data?.companies || [];
        
        // Fetch certificate status for each company
        const companiesWithCerts = await Promise.all(
          companiesList.map(async (company) => {
            try {
              const response = await certificatesService.getCertificates(company.id);
              const certs = response.data || [];
              const csd = computeCsdStatus(certs);
              return { 
                ...company, 
                csdStatus: csd.status,
                csdExpiresAt: csd.expiresAt ? csd.expiresAt.toISOString() : null,
                csdDaysLeft: csd.daysLeft
              };
            } catch (e) {
              return { ...company, csdStatus: 'unknown', csdExpiresAt: null, csdDaysLeft: null };
            }
          })
        );
        
        setCompanies(companiesWithCerts);
      } catch (error) {
        console.error('Error fetching companies:', error);
      }
    };

    fetchBalance();
    fetchCompanies();
  }, []);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  // Usar datos del balance actualizado del backend
  const planCredits = balance?.plan_credits || 0;
  const planCreditsUsed = balance?.plan_credits_used || 0;
  const planCreditsAvailable = balance?.plan_credits_available || 0;
  const bulkCredits = balance?.bulk_credits || 0;
  const totalCreditsAvailable = balance?.total_credits_available || 0;
  const creditUsagePercentage = balance?.credits_usage_percentage || 0;
  const needsRenewal = balance?.needs_renewal || false;
  const planRenewalDate = balance?.plan_renewal_date;

  return (
    <div>
      <Space align="center" style={{ marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0 }}>
          Dashboard
        </Title>
      </Space>

      {/* Alerta si necesita renovación */}
      {needsRenewal && (
        <Alert
          message="Plan Vencido"
          description="Tu plan ha vencido. Renueva tu suscripción para seguir disfrutando de los créditos mensuales."
          type="warning"
          icon={<WarningOutlined />}
          showIcon
          style={{ marginBottom: 24 }}
        />
      )}

      {/* Alerta si créditos bajos */}
      {totalCreditsAvailable <= 5 && totalCreditsAvailable > 0 && (
        <Alert
          message="Créditos Bajos"
          description={`Te quedan solo ${totalCreditsAvailable} créditos. Considera adquirir más créditos para continuar.`}
          type="warning"
          icon={<WarningOutlined />}
          showIcon
          style={{ marginBottom: 24 }}
        />
      )}

      {/* Alerta si no hay créditos */}
      {totalCreditsAvailable === 0 && (
        <Alert
          message="Sin Créditos"
          description="No tienes créditos disponibles. Adquiere más créditos para poder timbrar facturas."
          type="error"
          icon={<WarningOutlined />}
          showIcon
          style={{ marginBottom: 24 }}
        />
      )}

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic
              title="Créditos Totales Disponibles"
              value={totalCreditsAvailable}
              prefix={<CreditCardOutlined />}
              valueStyle={{ 
                color: totalCreditsAvailable > 10 ? '#3f8600' : 
                       totalCreditsAvailable > 0 ? '#faad14' : '#cf1322' 
              }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic
              title="Facturas Generadas"
              value={balance?.invoice_count || 0}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic
              title="Nóminas Generadas"
              value={balance?.nomina_count || 0}
              prefix={<TeamOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={12}>
          <Card>
            <Statistic
              title="Créditos del Plan"
              value={planCreditsAvailable}
              suffix={`/ ${planCredits}`}
              prefix={<CalendarOutlined />}
              valueStyle={{ color: planCreditsAvailable > 0 ? '#3f8600' : '#cf1322' }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={12}>
          <Card>
            <Statistic
              title="Créditos Adicionales"
              value={bulkCredits}
              prefix={<RiseOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="Uso de Créditos del Plan">
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span>Créditos Usados:</span>
                <span>{planCreditsUsed} / {planCredits}</span>
              </div>
              <Progress 
                percent={creditUsagePercentage} 
                status={creditUsagePercentage >= 100 ? 'exception' : 'active'}
                strokeColor={creditUsagePercentage >= 80 ? '#ff4d4f' : '#1890ff'}
              />
            </div>
            <div style={{ fontSize: 12, color: '#666' }}>
              {creditUsagePercentage >= 100 
                ? 'Has agotado los créditos de tu plan mensual'
                : `Te quedan ${planCreditsAvailable} créditos del plan`
              }
            </div>
            {planRenewalDate && (
              <div style={{ fontSize: 12, color: '#666', marginTop: 8 }}>
                <strong>Renovación:</strong> {new Date(planRenewalDate).toLocaleDateString('es-ES')}
              </div>
            )}
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Resumen de Créditos">
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Créditos del Plan Disponibles:</span>
                <span style={{ fontWeight: 'bold', color: planCreditsAvailable > 0 ? '#3f8600' : '#cf1322' }}>
                  {planCreditsAvailable}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Créditos Adicionales:</span>
                <span style={{ fontWeight: 'bold', color: '#722ed1' }}>
                  {bulkCredits}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #f0f0f0', paddingTop: 8 }}>
                <span><strong>Total Disponible:</strong></span>
                <span style={{ 
                  fontWeight: 'bold', 
                  fontSize: '16px',
                  color: totalCreditsAvailable > 10 ? '#3f8600' : 
                         totalCreditsAvailable > 0 ? '#faad14' : '#cf1322' 
                }}>
                  {totalCreditsAvailable}
                </span>
              </div>
              <div style={{ fontSize: 12, color: '#666', marginTop: 8 }}>
                <strong>Nota:</strong> Los créditos del plan se renuevan mensualmente. Los créditos adicionales vencen en 7 dias.
              </div>
            </Space>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24}>
          <Card 
            title={
              <Space>
                <ShopOutlined />
                <span>Empresas Registradas</span>
              </Space>
            }
          >
            {companies.length === 0 ? (
              <Alert message="No tienes empresas registradas" type="info" showIcon />
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
                {companies.map(company => (
                  <Card 
                    key={company.id} 
                    type="inner" 
                    size="small"
                    title={
                      <Text ellipsis style={{ maxWidth: '200px' }} title={company.tax_name}>
                        {company.tax_name}
                      </Text>
                    }
                    extra={
                      company.is_active ? 
                      <Text type="success" style={{ fontSize: '12px' }}><CheckCircleOutlined /> Activa</Text> : 
                      <Text type="secondary" style={{ fontSize: '12px' }}><CloseCircleOutlined /> Inactiva</Text>
                    }
                  >
                    <Space direction="vertical" style={{ width: '100%' }} size={4}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Text type="secondary">RFC:</Text>
                        <Text copyable>{company.tax_id}</Text>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Text type="secondary">Régimen:</Text>
                        <Text ellipsis style={{ maxWidth: '180px' }} title={company.fiscal_regimen}>
                          {company.fiscal_regimen || 'No definido'}
                        </Text>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, borderTop: '1px solid #f0f0f0', paddingTop: 8 }}>
                        <Text type="secondary">Certificado (CSD):</Text>
                        {company.csdStatus === 'valid' ? (
                          <Text type="success">
                            Vigente{company.csdExpiresAt ? ` (hasta ${new Date(company.csdExpiresAt).toLocaleDateString('es-MX')})` : ''}
                          </Text>
                        ) : company.csdStatus === 'expiring_soon' ? (
                          <Text type="warning">
                            Por vencer{company.csdExpiresAt ? ` (hasta ${new Date(company.csdExpiresAt).toLocaleDateString('es-MX')})` : ''}
                          </Text>
                        ) : company.csdStatus === 'expired' ? (
                          <Text type="danger">
                            Vencido{company.csdExpiresAt ? ` (${new Date(company.csdExpiresAt).toLocaleDateString('es-MX')})` : ''}
                          </Text>
                        ) : company.csdStatus === 'missing' ? (
                          <Text type="warning">Pendiente</Text>
                        ) : (
                          <Text type="secondary">Sin validar</Text>
                        )}
                      </div>
                    </Space>
                  </Card>
                ))}
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default UserDashboard;
